import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, BadgePercent } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { scrollTo } from "@/components/Nav";

// Noon-style symmetrical category strip: one horizontal scrollable row of
// equal-size round tiles (Deals first) with edge scroll arrows.
export default function Categories() {
  const { t, isAr } = useLanguage();
  const { categories } = useCart();
  const navigate = useNavigate();
  const rowRef = useRef(null);

  const scrollRow = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * dir * (isAr ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const tileCls =
    "group flex w-24 shrink-0 flex-col items-center gap-2 sm:w-32";
  const imgWrapCls =
    "h-24 w-24 overflow-hidden rounded-2xl border border-line bg-white shadow-sm transition-shadow duration-200 group-hover:shadow-md sm:h-32 sm:w-32";
  const labelCls = "text-center text-sm font-bold leading-tight text-brandink";

  return (
    <section id="collection" data-testid="categories-section" className="px-4 py-6 lg:px-8">
      <div className="relative mx-auto max-w-7xl">
        <button
          data-testid="category-scroll-prev"
          onClick={() => scrollRow(-1)}
          aria-label="Scroll categories back"
          className="absolute -start-3 top-14 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-brandink shadow-md transition-colors hover:bg-page md:flex"
        >
          <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>

        <div
          ref={rowRef}
          data-testid="categories-row"
          className="no-scrollbar flex gap-4 overflow-x-auto px-1 pb-2 pt-1 sm:gap-5"
        >
          <button data-testid="category-card-deals" onClick={() => scrollTo("#deals")} className={tileCls}>
            <span className={`${imgWrapCls} flex items-center justify-center bg-brand`}>
              <BadgePercent className="h-10 w-10 text-brandink sm:h-12 sm:w-12" />
            </span>
            <span className={labelCls}>{t("cat_deals")}</span>
          </button>

          {categories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`category-card-${cat.id}`}
              onClick={() => navigate(`/category/${cat.id}`)}
              className={tileCls}
            >
              <span className={imgWrapCls}>
                <img
                  src={cat.image}
                  alt={isAr ? cat.name_ar : cat.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </span>
              <span className={labelCls}>{isAr ? cat.name_ar : cat.name}</span>
            </button>
          ))}
        </div>

        <button
          data-testid="category-scroll-next"
          onClick={() => scrollRow(1)}
          aria-label="Scroll categories forward"
          className="absolute -end-3 top-14 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-brandink shadow-md transition-colors hover:bg-page md:flex"
        >
          <ChevronRight className="h-5 w-5 rtl:-scale-x-100" />
        </button>
      </div>
    </section>
  );
}
